import { COLOR_BANCO } from '@/lib/coloresBanco';
import { cn } from '@/lib/utils';
import type { BancoAsignado, BancoPago, CuentaDestino, PagoConDetalle } from '@adelante/shared';

/** Qué cambia de un banco a otro: el criterio de elección es la urgencia, no solo la comisión. */
export const AYUDA_BANCOS =
  'BAC: sin comisión, pero el archivo queda aplicado en la noche o al día siguiente. ' +
  'BN: se aplica inmediato; gratis hacia cuentas del BN (.TRF), con comisión hacia otros bancos (SINPE).';

const ACTIVO: Record<BancoPago, string> = {
  BAC: COLOR_BANCO.BAC.activo,
  BN: COLOR_BANCO.BN.activo,
};
const SUGERIDO: Record<BancoPago, string> = {
  BAC: COLOR_BANCO.BAC.suave,
  BN: COLOR_BANCO.BN.suave,
};

const OPCIONES: { valor: BancoAsignado; texto: string; titulo: string }[] = [
  {
    valor: 'BAC',
    texto: 'BAC',
    titulo: 'Se paga por BAC: sin comisión, se aplica en la noche o al día siguiente',
  },
  { valor: 'BN', texto: 'BN', titulo: 'Se paga por el Banco Nacional: se aplica inmediato' },
  { valor: 'MANUAL', texto: '—', titulo: 'Se paga a mano, sin archivo de banco' },
];

/** "…4066 · BN": los últimos dígitos del IBAN y el banco al que pertenece la cuenta. */
export function etiquetaCuenta(c: CuentaDestino): string {
  const banco = c.banco === 'OTRO' ? 'otro banco' : c.banco;
  return `…${c.iban.slice(-4)} · ${banco}`;
}

interface SelectorBancoProps {
  pago: PagoConDetalle;
  deshabilitado: boolean;
  /** `banco` es lo que se guarda en el pago; `null` = volver al banco por defecto del concepto. */
  onElegir: (banco: BancoAsignado | null) => void;
}

/**
 * Banco por el que se paga este pago: BAC, BN o "—" (a mano, sin archivo). Si todavía no se decidió,
 * el banco sugerido por la cuenta del proveedor se ve atenuado (el botón "Sugerir bancos" lo aplica).
 * Volver a pulsar la opción activa la quita (vuelve al banco por defecto del concepto, si lo hay).
 */
export function SelectorBanco({ pago, deshabilitado, onElegir }: SelectorBancoProps) {
  const activo = pago.bancoAsignado ?? pago.concepto.bancoPorDefecto;
  const sinDecidir = activo === null;
  return (
    <fieldset
      aria-label="Banco de pago"
      className="m-0 inline-flex min-w-0 overflow-hidden rounded-md border border-border p-0 text-[11px] font-medium"
    >
      {OPCIONES.map((o) => {
        const esActivo = activo === o.valor;
        const esSugerido = sinDecidir && o.valor !== 'MANUAL' && pago.bancoSugerido === o.valor;
        return (
          <button
            key={o.valor}
            type="button"
            aria-pressed={esActivo}
            disabled={deshabilitado}
            title={esSugerido ? `${o.titulo} — sugerido según la cuenta del proveedor` : o.titulo}
            onClick={() => onElegir(esActivo && pago.bancoAsignado === o.valor ? null : o.valor)}
            className={cn(
              'px-1.5 py-1 leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              esActivo
                ? o.valor === 'MANUAL'
                  ? 'bg-zinc-300 text-zinc-800'
                  : ACTIVO[o.valor as BancoPago]
                : esSugerido
                  ? SUGERIDO[o.valor as BancoPago]
                  : 'bg-transparent text-muted-foreground hover:bg-zinc-100',
            )}
          >
            {o.texto}
          </button>
        );
      })}
    </fieldset>
  );
}

interface LineaCuentaProps {
  pago: PagoConDetalle;
  deshabilitado: boolean;
  /** `null` = automática (la del mismo banco que el de salida). */
  onElegirCuenta: (cuentaId: number | null) => void;
}

/**
 * Bajo el concepto: a qué cuenta del proveedor se le paga y cómo viaja. Si el proveedor tiene varias
 * cuentas en esa moneda se puede cambiar la elegida. El aviso solo importa para el BN (BAC no cobra
 * comisión): a una cuenta del BN va por .TRF sin comisión; a otro banco, por SINPE con comisión.
 */
export function LineaCuentaDestino({ pago, deshabilitado, onElegirCuenta }: LineaCuentaProps) {
  const cuenta = pago.cuentaDestino;
  if (!cuenta || pago.bancoAsignado === 'MANUAL') return null;

  const aviso =
    pago.banco === 'BN' ? (
      cuenta.banco === 'BN' ? (
        <span className="text-muted-foreground">TRF · sin comisión</span>
      ) : (
        <span className="font-medium text-amber-700">SINPE · con comisión</span>
      )
    ) : null;

  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] leading-tight text-muted-foreground">
      <span title={cuenta.iban}>→ {etiquetaCuenta(cuenta)}</span>
      {aviso}
      {pago.cuentasDisponibles.length > 1 && (
        <select
          aria-label="Cuenta del proveedor a la que se paga"
          disabled={deshabilitado}
          value={pago.proveedorCuentaId ?? ''}
          onChange={(e) => onElegirCuenta(e.target.value ? Number(e.target.value) : null)}
          className="max-w-[9rem] rounded border border-border bg-transparent px-1 py-0.5 text-[11px] disabled:opacity-50"
        >
          <option value="">Automática</option>
          {pago.cuentasDisponibles.map((c) => (
            <option key={c.id} value={c.id}>
              {etiquetaCuenta(c)}
              {c.predeterminada ? ' (predet.)' : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
